import React from 'react'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  FormGroup,
  FormGroupProps,
  FormLabel,
  FormLabelProps,
} from '@material-ui/core'
import { MenuOption } from '../Menus'
import { every, isEqual, some } from 'lodash-es'


export type CheckboxGroupProps<T> = {
  name?: React.ReactNode
  value: T[] | undefined
  onChange: (value: T[]) => void
  options: MenuOption<T>[]
  areValuesEqual?: (a: T, b: T) => boolean
  FormGroupProps?: FormGroupProps
  FormLabelProps?: FormLabelProps
  FormControlLabelProps?: Partial<FormControlLabelProps>
  scrollable?: boolean
  maxHeight?: string | number
  disableSelectAll?: boolean
} & Partial<Pick<FormControlProps, 'color' | 'disabled' | 'size'>>

const stickyLabelCSS: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  backgroundColor: 'inherit',
  zIndex: 1,
}

export function CheckboxGroup<T>({
  name,
  value,
  options,
  onChange,
  areValuesEqual = isEqual,
  FormGroupProps = {},
  FormLabelProps = {},
  FormControlLabelProps = {},
  scrollable,
  maxHeight,
  disableSelectAll = false,
  ...props
}: CheckboxGroupProps<T>): JSX.Element {

  const enabledOptions = options.filter( o => !o.disabled)
  const allEnabledValuesEqual = every(enabledOptions, o => some(value, v => areValuesEqual(o.value, v)))

  const formGroupStyle: React.CSSProperties | undefined = (
    scrollable && maxHeight ?
      { maxHeight, overflowY: 'auto', flexWrap: 'nowrap', ...FormGroupProps.style } :
      FormGroupProps.style
  )

  const selectAllDisabled = (
    props.disabled
    || disableSelectAll
    || !enabledOptions.length
  )

  return (
    <FormControl color='secondary' component='fieldset' {...props}>
      { name && <FormLabel {...FormLabelProps}>{name}</FormLabel> }
      <FormGroup {...FormGroupProps} style={formGroupStyle}>
        <FormControlLabel
          name='select_all'
          disabled={selectAllDisabled}
          label={
            allEnabledValuesEqual && enabledOptions.length ?
              'Deselect All' :
              'Select All'
          }
          control={
            <Checkbox
              indeterminate
              disabled={selectAllDisabled}
              checked={allEnabledValuesEqual}
              onChange={(): void => {
                onChange(
                  allEnabledValuesEqual ?
                    [] :
                    enabledOptions.map( o => o.value )
                )
              }} />
          }
          style={scrollable ? stickyLabelCSS : undefined} />
        { options.map( (option, i) => {
          const isSelected = some(value, v => areValuesEqual(v, option.value))
          return (
            <FormControlLabel
              key={i}
              label={option.label}
              disabled={option.disabled || props.disabled}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={(): void => {
                    if( isSelected ){
                      onChange((value || []).filter( v => !areValuesEqual(v, option.value)))
                    }else{
                      onChange([...(value || []), option.value])
                    }
                  }} />
              }
              {...FormControlLabelProps} />
          )
        })}
      </FormGroup>
    </FormControl>
  )
}
